import React, { useEffect, useState } from 'react';
import adminApi from '../api/client';

interface Post {
  id: string;
  content: string;
  post_type: string;
  flag_reason: string;
  created_at: string;
  user_name: string;
  user_email: string;
  user_id: string;
}

interface ContentResponse {
  total: number;
  page: number;
  limit: number;
  posts: Post[];
}

const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);
  
  if (!message) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg text-white font-medium z-50 ${type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
      {message}
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: any) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-[#D85A30] text-white rounded font-medium hover:bg-opacity-90">Confirm</button>
        </div>
      </div>
    </div>
  );
};

export default function Content() {
  const [data, setData] = useState<ContentResponse>({ total: 0, page: 1, limit: 20, posts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState<{show: boolean, postId: string | null}>({ show: false, postId: null });

  const fetchFlaggedContent = async (currentPage: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminApi.get(`/admin/content/flagged?page=${currentPage}&limit=20`);
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch flagged content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlaggedContent(page);
  }, [page]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ show: true, message, type });
  };

  const handleApprove = async (id: string) => {
    try {
      await adminApi.post(`/admin/content/${id}/approve`);
      showToast('Post approved successfully', 'success');
      fetchFlaggedContent(page);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to approve post', 'error');
    }
  };

  const handleRemove = async () => {
    if (!confirmModal.postId) return;
    try {
      await adminApi.post(`/admin/content/${confirmModal.postId}/remove`);
      showToast('Post removed successfully', 'success');
      setConfirmModal({ show: false, postId: null });
      fetchFlaggedContent(page);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to remove post', 'error');
      setConfirmModal({ show: false, postId: null });
    }
  };

  return (
    <div className="bg-[#F1EFE8] min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-[#085041] mb-6">Content Moderation</h1>
        
        {error && (
          <div className="bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border-l-4 border-[#D85A30] flex items-center justify-between">
          <span className="font-semibold text-[#D85A30] text-lg">
            {data.total ?? 0} Flagged Posts
          </span>
          {loading && <span className="text-gray-500 text-sm">Loading...</span>}
        </div>

        {!loading && (!data.posts || data.posts.length === 0) ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500 text-lg flex flex-col items-center">
            <span className="text-4xl mb-4">✅</span>
            No flagged content
          </div>
        ) : (
          <div className="grid gap-6">
            {(data.posts || []).map((post) => (
              <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col gap-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1D9E75] text-white flex items-center justify-center font-bold text-lg">
                      {post.user_name ? post.user_name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div>
                      <div className="font-bold text-[#085041]">{post.user_name || 'Unknown User'}</div>
                      <div className="text-sm text-gray-500">{post.user_email}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(post.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded text-gray-800 italic">
                  "{post.content && post.content.length > 200 ? post.content.substring(0, 200) + '...' : post.content}"
                </div>

                <div className="flex items-center justify-between mt-2">
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold uppercase tracking-wider">
                      {post.post_type}
                    </span>
                    <span className="px-3 py-1 bg-[#D85A30]/10 text-[#D85A30] rounded-full text-xs font-semibold tracking-wider flex items-center gap-1">
                      <span className="text-base leading-none">🚩</span> {post.flag_reason}
                    </span>
                  </div>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleApprove(post.id)}
                      className="flex items-center gap-1 px-4 py-2 border border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75] hover:text-white transition-colors rounded font-medium"
                    >
                      ✓ Approve
                    </button>
                    <button 
                      onClick={() => setConfirmModal({ show: true, postId: post.id })}
                      className="flex items-center gap-1 px-4 py-2 bg-[#D85A30] text-white hover:bg-opacity-90 transition-colors rounded font-medium"
                    >
                      ✗ Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {data.total > 0 && Math.ceil((data.total ?? 0) / (data.limit || 20)) > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button 
              disabled={page === 1} 
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700">
              Page {page} of {Math.ceil((data.total ?? 0) / (data.limit || 20))}
            </span>
            <button 
              disabled={page >= Math.ceil((data.total ?? 0) / (data.limit || 20))} 
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <Toast 
        message={toast.show ? toast.message : ''} 
        type={toast.type} 
        onClose={() => setToast(prev => ({ ...prev, show: false }))} 
      />
      
      <ConfirmModal 
        isOpen={confirmModal.show} 
        onClose={() => setConfirmModal({ show: false, postId: null })}
        onConfirm={handleRemove}
        title="Remove Post"
        message="Are you sure you want to remove this post? This action cannot be undone."
      />
    </div>
  );
}
